import { useEffect, useState } from 'react'

/**
 * How long an exit animation may run before the element unmounts. Matches
 * `--motion-fast` (160ms) plus a frame; the CSS holds the end state with
 * `animation-fill-mode: forwards`, so a slightly longer timer is harmless.
 */
export const EXIT_MS = 180

// jsdom declares matchMedia but leaves it undefined.
const hasMatchMedia = () => typeof (window as Partial<Window>).matchMedia === 'function'

/**
 * Whether a closing surface should stay mounted for its exit animation.
 * Not without matchMedia (jsdom: tests see the element go at once) and not
 * under `prefers-reduced-motion: reduce` (nothing would move anyway).
 */
export function canAnimateExit(): boolean {
  if (!hasMatchMedia()) return false
  return !window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

export interface Presence {
  /** Render the surface. True while open and during the exit animation. */
  isMounted: boolean
  /** Closing: render with `data-state="closed"`, `inert`, and let it animate out. */
  isClosing: boolean
}

/**
 * Keep a surface mounted briefly after `isOpen` turns false so it can animate
 * out (ADR 0019). Everything that matters for behaviour — focus restore,
 * inert, Escape — keys off `isOpen`, never off this, so a closing surface is
 * already gone for keyboard and assistive technology.
 */
export function usePresence(isOpen: boolean, exitMs = EXIT_MS): Presence {
  const [isRetained, setRetained] = useState(false)
  const [wasOpen, setWasOpen] = useState(isOpen)

  // Open → closed: retain for the exit, if motion is allowed. Adjusting state
  // during render keeps the closing frame from flashing unmounted.
  if (wasOpen !== isOpen) {
    setWasOpen(isOpen)
    setRetained(!isOpen && canAnimateExit())
  }

  useEffect(() => {
    if (!isRetained) return
    const timer = window.setTimeout(() => {
      setRetained(false)
    }, exitMs)
    return () => {
      window.clearTimeout(timer)
    }
  }, [isRetained, exitMs])

  return { isMounted: isOpen || isRetained, isClosing: !isOpen && isRetained }
}

interface Leaving<T> {
  item: T
  /** Where it stood, so it animates out in place rather than jumping to the end. */
  index: number
}

/**
 * The list form of `usePresence`, for stacks such as the notifier: items that
 * leave `items` are kept in place for `exitMs` and flagged as leaving.
 */
export function usePresenceList<T extends { id: string | number }>(
  items: readonly T[],
  exitMs = EXIT_MS
): { item: T; isLeaving: boolean }[] {
  const [previous, setPrevious] = useState(items)
  const [leaving, setLeaving] = useState<Leaving<T>[]>([])

  if (previous !== items) {
    setPrevious(items)
    if (canAnimateExit()) {
      const ids = new Set(items.map((item) => item.id))
      const gone = previous
        .map((item, index) => ({ item, index }))
        .filter(({ item }) => !ids.has(item.id))
      if (gone.length > 0) {
        setLeaving((current) => [...current.filter((l) => !ids.has(l.item.id)), ...gone])
      }
    }
  }

  useEffect(() => {
    if (leaving.length === 0) return
    const timer = window.setTimeout(() => {
      setLeaving([])
    }, exitMs)
    return () => {
      window.clearTimeout(timer)
    }
  }, [leaving, exitMs])

  const result = items.map((item) => ({ item, isLeaving: false }))
  for (const { item, index } of leaving) {
    if (items.some((i) => i.id === item.id)) continue
    result.splice(Math.min(index, result.length), 0, { item, isLeaving: true })
  }
  return result
}
